// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import { resolveMatchups, canAutoPopulate } from '@/utils/scoresheets/matchups';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { Camera, Loader2 } from 'lucide-react';
import ConfidenceBadge from './ConfidenceBadge';

import useGameStore from '@/store/gameStore';
import useTeamDivisionStore from '@/store/teamDivisionStore';
import useScoresheetStore from '@/store/scoresheetStore';

const CONF_RANK = { high: 3, 'medium-high': 2, medium: 1, low: 0 };
const worseConfidence = (a, b) => (CONF_RANK[a] <= CONF_RANK[b] ? a : b) || 'medium';

export default function ScoresheetScanPickleball({
  division, org, open, onOpenChange, onSubmitted,
}) {
  const { getToken } = useAuth();
  const divisionId = helpers.getObjId(division);
  const leagueId = helpers.getObjId(division.league);
  const orgId = helpers.getObjId(org) || helpers.getObjId(division.organization);

  const { teamDivisions, getTeamDivisionsByDivisionId } = useTeamDivisionStore((s) => ({
    teamDivisions: s.teamDivisions, getTeamDivisionsByDivisionId: s.getTeamDivisionsByDivisionId,
  }));
  const { games, getGamesByDivisionId } = useGameStore((s) => ({
    games: s.games, getGamesByDivisionId: s.getGamesByDivisionId,
  }));
  const updateGame = useGameStore((s) => s.updateGame(getToken));
  const addBulkGames = useGameStore((s) => s.addBulkGames(getToken));
  const { uploadScoresheet, parseScoresheet, pollScoresheetJob } = useScoresheetStore((s) => ({
    uploadScoresheet: s.uploadScoresheet, parseScoresheet: s.parseScoresheet, pollScoresheetJob: s.pollScoresheetJob,
  }));

  useEffect(() => {
    if (open && divisionId) {
      getTeamDivisionsByDivisionId(divisionId);
      getGamesByDivisionId(divisionId);
    }
  }, [open, divisionId]);

  // Division teams ordered by seed (seed N → orderedTeams[N-1]), matching how the
  // printable scoresheet + auto-populate seed the round-robin.
  const orderedTeams = useMemo(() => {
    const dts = teamDivisions.filter((t) => helpers.getObjId(t.division) === divisionId);
    return [...dts].sort((a, b) => (Number(a.seed || 9999) - Number(b.seed || 9999)));
  }, [teamDivisions, divisionId]);

  const teamName = (id) => {
    const t = orderedTeams.find((x) => helpers.getObjId(x) === id);
    return t ? getTeamDisplayName(t) : 'TBD';
  };

  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setPhase('idle'); setRows([]); setError(''); }
  }, [open]);

  const format = division?.config?.format;
  const supported = canAutoPopulate(format);

  const findGame = (round, homeTeamId, awayTeamId) =>
    games.find((g) =>
      Number(_.get(g, 'competitionMeta.round')) === Number(round)
      && helpers.getObjId(g.homeTeam) === homeTeamId
      && helpers.getObjId(g.awayTeam) === awayTeamId);

  const buildRows = (result) => {
    const matchups = resolveMatchups(format, { division, teams: orderedTeams });
    const seedOf = new Map(orderedTeams.map((t, i) => [helpers.getObjId(t), i + 1]));
    // round -> seed -> { points, confidence }
    const grid = {};
    (result.rounds || []).forEach((r) => {
      grid[r.round] = {};
      (r.results || []).forEach((x) => { grid[r.round][x.teamSeed] = x; });
    });
    return matchups.map((m, i) => {
      const hSeed = seedOf.get(m.homeTeamId);
      const aSeed = seedOf.get(m.awayTeamId);
      const h = grid[m.round]?.[hSeed];
      const a = grid[m.round]?.[aSeed];
      const existing = findGame(m.round, m.homeTeamId, m.awayTeamId);
      return {
        key: `m-${i}`,
        round: m.round,
        pool: m.pool,
        homeTeamId: m.homeTeamId,
        awayTeamId: m.awayTeamId,
        homeScore: h?.points != null ? String(h.points) : '',
        awayScore: a?.points != null ? String(a.points) : '',
        confidence: worseConfidence(h?.confidence, a?.confidence),
        gameId: existing?._id || null,
        game: existing || null,
      };
    });
  };

  const onPickPhoto = async (file) => {
    if (!file) return;
    setPhase('uploading'); setError('');
    try {
      const token = await getToken();
      const imageUrl = await uploadScoresheet(file, orgId, token);
      setPhase('analyzing');
      const { jobId } = await parseScoresheet({
        sport: 'pickleball',
        imageUrl,
        divisionId,
        teams: orderedTeams.map((t, i) => ({ seed: i + 1, label: getTeamDisplayName(t) })),
      }, token);
      const result = await pollScoresheetJob(jobId, token);
      setRows(buildRows(result));
      setPhase('done');
    } catch (e) {
      console.error('pickleball scan failed', e);
      setPhase('error'); setError(e?.message || 'Scan failed');
      toast.error(e?.message || 'Scoresheet scan failed');
    }
  };

  const updateRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const toCreate = [];
      for (const row of rows) {
        if (row.homeScore === '' && row.awayScore === '') continue;
        const homeScore = row.homeScore === '' ? null : Number(row.homeScore);
        const awayScore = row.awayScore === '' ? null : Number(row.awayScore);
        if (row.game) {
          await updateGame({
            ...row.game,
            homeTeam: row.homeTeamId,
            awayTeam: row.awayTeamId,
            homeScore, awayScore,
          });
        } else {
          toCreate.push({
            division: divisionId,
            organization: orgId,
            league: leagueId,
            homeTeam: row.homeTeamId,
            awayTeam: row.awayTeamId,
            homeScore, awayScore,
            sport: 'pickleball',
            competitionMeta: { round: row.round, ...(row.pool != null ? { pool: row.pool } : {}) },
          });
        }
      }
      if (toCreate.length) await addBulkGames(toCreate);
      toast.success('Division results saved');
      onSubmitted && onSubmitted();
      onOpenChange(false);
    } catch (e) {
      console.error('submit failed', e);
      toast.error('Failed to save results');
    } finally {
      setSubmitting(false);
    }
  };

  const busy = phase === 'uploading' || phase === 'analyzing';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scan division results: {division?.name}</DialogTitle>
          <DialogDescription>
            Take one photo of the division round-robin scoresheet. The AI reads every matchup&apos;s
            score; review and edit before submitting all games at once.
          </DialogDescription>
        </DialogHeader>

        {!supported ? (
          <div className="text-sm text-muted-foreground">
            This division&apos;s format ({format || 'unset'}) is not a round-robin / pool-play format,
            so per-division scoresheet scanning isn&apos;t available.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="inline-flex">
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => onPickPhoto(e.target.files?.[0])} />
                <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border cursor-pointer text-sm hover:bg-accent">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                  {phase === 'uploading' ? 'Uploading…'
                    : phase === 'analyzing' ? 'Reading scoresheet…'
                    : phase === 'done' ? 'Retake photo' : 'Take / upload photo'}
                </span>
              </label>
            </div>

            {error && <div className="text-sm text-destructive">{error}</div>}

            {phase === 'done' && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Round</TableHead>
                      <TableHead>Home</TableHead>
                      <TableHead className="w-20 text-center">Score</TableHead>
                      <TableHead>Away</TableHead>
                      <TableHead className="w-20 text-center">Score</TableHead>
                      <TableHead className="w-28">Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.round}{row.pool != null ? ` · P${row.pool + 1}` : ''}</TableCell>
                        <TableCell className="p-1">
                          <select className="h-8 w-full rounded-md border bg-background text-sm px-1"
                            value={row.homeTeamId}
                            onChange={(e) => updateRow(row.key, { homeTeamId: e.target.value })}>
                            {orderedTeams.map((t) => (
                              <option key={helpers.getObjId(t)} value={helpers.getObjId(t)}>{getTeamDisplayName(t)}</option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="p-1 text-center">
                          <Input type="text" inputMode="numeric" className="h-8 w-16 mx-auto text-center"
                            value={row.homeScore}
                            onChange={(e) => updateRow(row.key, { homeScore: e.target.value.replace(/[^0-9]/g, '') })} />
                        </TableCell>
                        <TableCell className="p-1">
                          <select className="h-8 w-full rounded-md border bg-background text-sm px-1"
                            value={row.awayTeamId}
                            onChange={(e) => updateRow(row.key, { awayTeamId: e.target.value })}>
                            {orderedTeams.map((t) => (
                              <option key={helpers.getObjId(t)} value={helpers.getObjId(t)}>{getTeamDisplayName(t)}</option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="p-1 text-center">
                          <Input type="text" inputMode="numeric" className="h-8 w-16 mx-auto text-center"
                            value={row.awayScore}
                            onChange={(e) => updateRow(row.key, { awayScore: e.target.value.replace(/[^0-9]/g, '') })} />
                        </TableCell>
                        <TableCell><ConfidenceBadge level={row.confidence} /></TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        No matchups resolved for this division.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={phase !== 'done' || submitting || rows.length === 0}>
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Submit all results
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
